import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { withRouter } from 'react-router-dom'
import { i18nTranslate, getShoppingContext, isFeatureEnabled } from 'src/utils'
import {
  MetaDecorator,
  DrawerModal,
  toastState,
  SigninModal,
} from 'src/views/components'
import { MicroFrontendLoader } from 'src/views/components/MicroFrontends/MicroFrontendLoader'
import { toPlainSnapshot } from 'src/views/components/MicroFrontends/mfeSnapshot'
import { NoOfferHere } from 'src/views/components/NoOfferHere'
import loadable from '@loadable/component'
import {
  customerContainer,
  personalOfferContainer,
  storeContainer,
  checkoutContainer,
  cartContainer,
  accountsContainer,
} from 'src/models'
import {
  APPConfig,
  convertToBoolean,
  getBuyNowForPO,
  setLocalStorage,
  getSessionStorage,
  setSessionStorage,
  deleteFromLocalStorage,
  enableCheckoutMFE,
} from 'config/appConfig'
import {
  enablePORedesignFeature,
  isGuestCheckoutEnabledForWeb,
} from 'src/utils/commonUtils'
import { getPOAnalyticsSessionId } from 'src/utils/poAnalyticsUtils'
import { subscribe, unSubscribeEvent } from 'src/pubsub/subscribe'
import { PUBSUB } from 'src/pubsub/pubEventNames'
import { trackPathToPurchaseBeginCheckout } from 'src/utils'
import { redirectToLocaleHome, redirectToSignIn } from 'src/utils/mySiteUtils'
import {
  DEFAULT_SPONSOR,
  CART_TYPE,
  AVAILABLE_CHANNELS,
  MEMBER_PRICE,
} from 'src/utils/constant'
import { IoIosArrowBack } from 'react-icons/io'
import Skeleton from 'react-loading-skeleton'
import NotFound from '../NotFound'
import PersonalOfferPDPLoader from '../PersonalOfferPDPLoader'
import ExpressCheckoutSkeleton from './ExpressCheckoutSkeleton'
import './styles.scss'

// Loaded on demand: PersonalOffer is statically reachable from StudioPage (the
// home page), so a static import here would ship the whole express checkout
// tree (incl. nu-taxid, ~5 MB raw) to every home page visitor.
const ExpressCheckout = loadable(
  () => import('src/views/pages/ExpressCheckout/ExpressCheckout'),
  {
    resolveComponent: mod => mod.ExpressCheckout,
    fallback: <ExpressCheckoutSkeleton />,
  }
)

const isVariantBackOrdered = (variant = {}) =>
  variant?.status?.inventory === 'backordered' ||
  !!variant?.backOrdered ||
  !!variant?.inventoryProperties?.backOrdered

const isProductBackOrdered = (product = {}) => {
  const firstVariant = product?.variants?.[0]
  if (isVariantBackOrdered(firstVariant) || !!product?.backOrderedTimeStamp) {
    return true
  }
  const kitProducts = product?.bundle?.kitProducts
  if (Array.isArray(kitProducts)) {
    return kitProducts.some(kit =>
      (kit?.product?.variants ?? []).some(v => isVariantBackOrdered(v))
    )
  }
  return false
}

@observer
class PersonalOffer extends React.Component {
  @observable isExpressBuyNowLoading = false
  @observable isSigninModalVisible = false
  @observable mfeNoOfferRequest = null
  poBuyNowSubscription = null
  poShowAuthPopupSubscription = null
  lastTrackedPitchId = ''
  poNoOfferSubscription = null

  get translations() {
    const csValues = personalOfferContainer?.productOffersCSValues || {}
    return {
      backToOffer: csValues?.labelBackToOffer || 'Back to Offer',
      pageTitle: csValues?.title || 'Nu Skin | Personal Offer',
      expressCheckoutModal:
        csValues?.labelExpressCheckoutModal || 'Express Checkout Modal',
    }
  }

  constructor(props) {
    super(props)
    // Clear legacy LS pollution so it can't shadow admin config (delete-only, never written).
    deleteFromLocalStorage('directSellerAttributeId')
    deleteFromLocalStorage('directSellerAttributeValue')
  }

  componentDidMount() {
    if (getBuyNowForPO() && enablePORedesignFeature()) {
      this.poBuyNowSubscription = subscribe(
        PUBSUB.PERSONAL_OFFER_BUY_NOW,
        this.handlePOBuyNow
      )
      this.poShowAuthPopupSubscription = subscribe(
        PUBSUB.PERSONAL_OFFER_SHOW_AUTH_POPUP,
        () => this.toggleSigninModal(true)
      )
    }
    this.poNoOfferSubscription = subscribe(
      PUBSUB.PO_SHOW_NO_OFFER_HERE,
      this.handleShowNoOfferHere
    )
    this.loadCSTranslations()
    this.triggerAnalyticsTracking()
  }

  componentDidUpdate() {
    this.triggerAnalyticsTracking()
  }

  getPersonalOfferUrlParams = () => {
    if (typeof window === 'undefined') {
      return { userId: '', pitchId: '', isPreview: false }
    }
    const paramsFromURL = new URLSearchParams(window.location.search || '')
    const pitchId = paramsFromURL.get('pitchId') || ''
    const isPreview = paramsFromURL.has('preview')
    const pathSegments = (window.location.pathname || '')
      .split('/')
      .filter(Boolean)
    const userId = pathSegments[pathSegments.length - 1] || ''
    return { userId, pitchId, isPreview }
  }

  triggerAnalyticsTracking = () => {
    if (!enablePORedesignFeature()) return

    const { userId, pitchId, isPreview } = this.getPersonalOfferUrlParams()
    if (!userId || !pitchId || isPreview) return

    const personalOfferData = getSessionStorage('personalOffer') || {}
    const responseStatus =
      personalOfferData?.responseStatus ||
      personalOfferContainer?.personalOfferData?.responseStatus ||
      ''
    if (responseStatus !== 'active') return

    if (this.lastTrackedPitchId === pitchId) return

    // `po_audit_sessionId` is an `{ offerId: analyticsSessionId }` map. If
    // the current offer already has a sessionId, reuse it without making a
    // fresh analytics call.
    const existingSessionId = getPOAnalyticsSessionId(pitchId)

    if (existingSessionId) {
      this.lastTrackedPitchId = pitchId
      return
    }

    this.lastTrackedPitchId = pitchId
    personalOfferContainer.trackAnalytics(pitchId)
  }

  loadCSTranslations = async () => {
    try {
      if (!personalOfferContainer?.productOffersCSValues) {
        await personalOfferContainer.getProductOffersCSValues()
      }
    } catch (error) {
      console.error('Error loading PO translations:', error)
    }
  }

  componentWillUnmount() {
    if (this.poBuyNowSubscription) {
      unSubscribeEvent(this.poBuyNowSubscription)
      this.poBuyNowSubscription = null
    }
    if (this.poShowAuthPopupSubscription) {
      unSubscribeEvent(this.poShowAuthPopupSubscription)
      this.poShowAuthPopupSubscription = null
    }
    if (this.poNoOfferSubscription) {
      unSubscribeEvent(this.poNoOfferSubscription)
      this.poNoOfferSubscription = null
    }
  }

  handleShowNoOfferHere = (_, data) => {
    this.mfeNoOfferRequest = {
      userId: data?.userId || '',
      pitchId: data?.pitchId || '',
      reason: data?.reason || 'invalid_link',
    }
  }

  handlePOBuyNow = async (_, data) => {
    const {
      products = [],
      sponsorId = '',
      pitchId = '',
      totalPrice = 0,
      hasNonRetailProduct = false,
      isGuestCheckout = false,
      offerPriceType = '',
      source = '',
    } = data || {}

    if (!products.length) return

    if (
      this.shouldShowSigninPopupOnBuyNow(hasNonRetailProduct, offerPriceType)
    ) {
      redirectToSignIn()
      return
    }

    this.isExpressBuyNowLoading = true
    setLocalStorage('isExpressCart', true)
    setSessionStorage('po_buy_now', 'true')

    try {
      if (!isGuestCheckout) {
        await cartContainer.viewCart()
      }

      // Clear existing EXPRESS cart items
      if (
        cartContainer.cartResponse?.items?.length &&
        cartContainer.cartResponse?.cartType === CART_TYPE.EXPRESS
      ) {
        await cartContainer.deleteCartItem(
          '',
          false,
          '',
          false,
          '',
          '',
          '',
          null,
          true
        )
      }

      // Add all selected PO products to express cart
      // MFE sends { product: { id, variants, bundle, bundleMandatoryProducts, ... }, quantity } per item
      const cartArray = products.map(item => {
        const product = item?.product || item
        const quantity = item?.quantity || 1

        // Detect bundle: storefront-transformed uses bundleMandatoryProducts,
        // raw GraphQL uses bundle.kitProducts (bundle is an object, not array)
        const bundleMandatoryProducts = product?.bundleMandatoryProducts || []
        const kitProducts = product?.bundle?.kitProducts || []
        const isBundle =
          product?.type?.toLowerCase() === 'bundle' ||
          product?.bundle?.type?.toLowerCase() === 'bundle' ||
          bundleMandatoryProducts.length > 0 ||
          kitProducts.length > 0

        if (isBundle) {
          // Parent BUNDLE entry uses productId as both productId AND skuId
          const bundleProductId = product?.productId || product?.id || ''
          const parentAvailableChannels =
            product?.properties?.availableChannels || AVAILABLE_CHANNELS.DEFAULT
          const bundleParentType =
            product?.bundle?.type?.toUpperCase() ||
            product?.type?.toUpperCase() ||
            'BUNDLE'
          const parentBackOrdered = isProductBackOrdered(product)
          const skus = [
            {
              productId: bundleProductId,
              skuId: bundleProductId,
              type: bundleParentType,
              availableChannels: parentAvailableChannels,
              backOrdered: parentBackOrdered,
            },
          ]

          if (bundleMandatoryProducts.length > 0) {
            // Storefront-transformed format: { identifier, selectedSku: { identifier, ... }, quantity }
            bundleMandatoryProducts.forEach(bp => {
              if (bp?.selectedSku) {
                skus.push({
                  productId: bp?.identifier || '',
                  skuId: bp?.selectedSku?.identifier || '',
                  type: 'MANDATORY',
                  skuQuantity: String(
                    bp?.quantity || bp?.properties?.quantity || 1
                  ),
                  availableChannels:
                    bp?.selectedSku?.skuProperties?.availableChannels ||
                    AVAILABLE_CHANNELS.WITH_SUBSCRIPTION,
                  backOrdered: isVariantBackOrdered(bp?.selectedSku),
                  slug: bp?.properties?.slug || '',
                })
              }
            })
          } else if (kitProducts.length > 0) {
            // Raw GraphQL format: bundle.kitProducts = [{ isMandatory, product: { id, variants: [{ sku }] } }]
            kitProducts.forEach(kp => {
              const childProduct = kp?.product || {}
              const childVariant = childProduct?.variants?.[0] || {}
              skus.push({
                productId: childProduct?.id || '',
                skuId: childVariant?.sku || '',
                type: kp?.isMandatory ? 'MANDATORY' : 'OPTIONAL',
                skuQuantity: '1',
                availableChannels: AVAILABLE_CHANNELS.WITH_SUBSCRIPTION,
                backOrdered: isVariantBackOrdered(childVariant),
                slug: childProduct?.slug || '',
              })
            })
          }

          return {
            quantity,
            skus,
            isBundle: true,
            productId: bundleProductId,
            isBackOrdered: parentBackOrdered,
            brand:
              product?.variants?.[0]?.brandFamily ||
              product?.brandFamily ||
              product?.brand ||
              '',
            imageURL:
              product?.variants?.[0]?.productImages?.[0]?.url ||
              product?.productImages?.[0]?.url ||
              '',
            name: product?.title || product?.name || '',
            slug: product?.slug || '',
          }
        }

        const simpleBackOrdered = isProductBackOrdered(product)
        return {
          quantity,
          skuId:
            product?.variants?.[0]?.sku || product?.sku || product?.skuId || '',
          productId: product?.productId || product?.id || '',
          isBackOrdered: simpleBackOrdered,
        }
      })

      const response = await cartContainer.addToCart({
        cartArray,
        isShowToast: false,
        source,
      })

      if (checkoutContainer.isSuccessResponse(response)) {
        const cartItems = cartContainer?.cartResponse?.items || []
        const cartValue = cartContainer?.cartResponse?.value || {}
        trackPathToPurchaseBeginCheckout({
          responseData: cartItems,
          sponsorId:
            sponsorId ||
            accountsContainer?.getAccountSponsorId?.() ||
            DEFAULT_SPONSOR,
          discount: cartValue?.itemDiscount || 'NA',
          item_list_name: 'po_express_checkout',
          item_persistent: false,
          totalValue: cartValue?.grandTotal || totalPrice || 0,
        })

        this.isExpressBuyNowLoading = false
        checkoutContainer.renderExpressPDPCart =
          checkoutContainer.showExpressCheckoutModalBody =
          checkoutContainer.showExpressCheckoutDrawer =
            true
      } else {
        this.isExpressBuyNowLoading = false
        setLocalStorage('isExpressCart', false)
        const errorCode = response?.errorCode || response?.code || ''
        if (errorCode === 'EOIND00013' || errorCode === 'EOPLOR001') {
          toastState.setToastMessage(
            i18nTranslate(
              'cart.homeMarket',
              'Please switch to your home market to make a purchase.'
            ),
            false
          )
        }
      }
    } catch (error) {
      console.error('Error in handlePOBuyNow:', error)
      this.isExpressBuyNowLoading = false
      setLocalStorage('isExpressCart', false)
    }
  }

  toggleExpressCheckoutModal = async () => {
    checkoutContainer.showExpressCheckoutDrawer =
      checkoutContainer.renderExpressPDPCart =
      checkoutContainer.showExpressCheckoutModalBody =
        false
    setSessionStorage('po_buy_now', 'false')
    try {
      if (
        cartContainer.cartResponse?.items?.length &&
        cartContainer.cartResponse?.cartType === CART_TYPE.EXPRESS
      ) {
        await cartContainer.deleteCartItem(
          '',
          false,
          '',
          false,
          '',
          '',
          '',
          null,
          true
        )
      }
      setLocalStorage('isExpressCart', false)
      cartContainer.viewCart()
    } catch (error) {
      console.error('Error in toggleExpressCheckoutModal:', error)
    }
  }

  /**
   * Decides whether to show the SignIn/SignUp popup before allowing the user
   * to proceed with the PO Buy Now express checkout flow. Mirrors the logic
   * used by the PDP Buy Now button (V2ProductQuantityAndButtons).
   */
  shouldShowSigninPopupOnBuyNow = (
    hasNonRetailProduct = false,
    offerPriceType = ''
  ) => {
    const shoppingContext = getShoppingContext() || {}
    const enableGuestForWeb = isGuestCheckoutEnabledForWeb()
    const enableGuestForPoMysite = convertToBoolean(
      APPConfig?.getAppConfig()?.enableGuestCheckoutForPOMysite
    )
    let isGuestCheckoutEnabled = false
    if (Object.keys(shoppingContext).length > 0 && enableGuestForPoMysite) {
      const wholesale =
        shoppingContext?.overrides?.showWholeSalePricing || false
      isGuestCheckoutEnabled = !wholesale
    } else if (enableGuestForWeb) {
      isGuestCheckoutEnabled = true
    }
    // If the personal offer is configured with member pricing, guests must
    // sign in / sign up before they can proceed with Buy Now.
    const priceType = (
      offerPriceType ||
      personalOfferContainer?.personalOfferData?.priceType ||
      personalOfferContainer?.poPriceType ||
      ''
    )
      .toString()
      .toLowerCase()
    const isMemberPriceType = priceType === MEMBER_PRICE
    if (!customerContainer.isRegisterUser && isMemberPriceType) {
      return true
    }
    // If any selected product lacks 'Retail' in customerTypes, a guest user
    // cannot complete express checkout (ExpressCheckout redirects back to
    // view-cart). Show Sign In modal upfront instead — same as .com behavior.
    if (!customerContainer.isRegisterUser && hasNonRetailProduct) {
      return true
    }
    return !customerContainer.isRegisterUser && !isGuestCheckoutEnabled
  }

  toggleSigninModal = visibility => {
    this.isSigninModalVisible =
      visibility !== undefined ? visibility : !this.isSigninModalVisible
  }

  renderExpressCheckout = () => {
    return enableCheckoutMFE() ? (
      <MicroFrontendLoader
        remoteConfig={{
          appName: 'checkout-mfe',
          component: 'ExpressCheckoutPage',
          type: 'global',
          entryGlobalName: 'checkout-mfe',
        }}
        params={{
          routerHistory: this.props.history,
          // Identity-stable plain snapshots — see mfeSnapshot.js.
          initialCartData: toPlainSnapshot(cartContainer?.cartResponse) || {},
          customerData:
            toPlainSnapshot(customerContainer?.profileResponse) || {},
          isFromPDP: true,
          isFromSideDrawer: true,
          // Mirror the non-MFE branch (see below): the PO Buy Now drawer
          // does not render a member-agreement checkbox, so the
          // isFromSideDrawer + enableExpressCheckoutMemberAgreement gate
          // in the express PayPal handler must be bypassed. isFromPO is
          // the same discriminator the non-MFE ExpressCheckout uses to
          // skip that gate.
          isFromPO: true,
        }}
      />
    ) : (
      <ExpressCheckout
        isFromPDP={true}
        isFromPO={true}
        history={this.props?.history}
      />
    )
  }

  renderDrawer = () => {
    const { backToOffer, expressCheckoutModal } = this.translations
    return (
      <DrawerModal
        renderView={
          checkoutContainer.showExpressCheckoutModalBody
            ? this.renderExpressCheckout
            : () => <ExpressCheckoutSkeleton />
        }
        onHide={this.toggleExpressCheckoutModal}
        show={checkoutContainer.showExpressCheckoutDrawer}
        showCloseButton={false}
        testId="qa-po-express-buy-now-modal"
        label={expressCheckoutModal}
        title={
          <>
            {!checkoutContainer.showExpressCheckoutModalBody ? (
              <Skeleton rectangle={true} height={20} width={400} />
            ) : (
              <div
                role="button"
                className="modal-custom-title"
                tabIndex={0}
                onClick={this.toggleExpressCheckoutModal}>
                <IoIosArrowBack />
                <span>{backToOffer}</span>
              </div>
            )}
          </>
        }
        customClass="cart-single-subscription-modal modal-dialog-scrollable express-checkout-modal"
      />
    )
  }

  render() {
    const prTitle = this.translations.pageTitle
    const remoteConfig = {
      appName: 'personaloffer-mfe',
      component: 'App',
    }
    const activeStoreData = {
      defaultCurrency:
        storeContainer?.activeStoreResponse?.defaultCurrency || '',
      defaultLocale: storeContainer?.activeStoreResponse?.defaultLocale || '',
    }

    // Extract products from personalOfferContainer
    const isProductOfferV2Enabled = enablePORedesignFeature()
    const products = isProductOfferV2Enabled
      ? personalOfferContainer?.personalOfferData?.products || []
      : personalOfferContainer?.personalOfferData?.shareLink?.products || []
    // Use getActiveAppConfig (window.APP_CONFIG) to bypass ConfigKey's LS shadow.
    const activeAppConfig = APPConfig?.getActiveAppConfig?.() || {}
    const directSellerAttributeId =
      activeAppConfig.directSellerAttributeId || 'locallyProduced'
    const directSellerAttributeValue =
      activeAppConfig.directSellerAttributeValue || 'false'
    const enableNarrowSymbolCurrencyFormat = isFeatureEnabled(
      'enableNarrowSymbolCurrencyFormat'
    )
    const enableDecimalRoundingInPrice = isFeatureEnabled(
      'enableDecimalRoundingInPrice'
    )
    const excludeSpaceInPrice = isFeatureEnabled('excludeSpaceInPrice')
    const params = {
      isRegisterUser: customerContainer.isRegisterUser,
      customerType: customerContainer?.type,
      isLoadFromMfe: true,
      activeStoreResponse: activeStoreData || {},
      customerData: customerContainer?.profileResponse || {},
      personalOfferData: {
        isValidSponsor: personalOfferContainer.poSiteSponsor ? true : false,
        bannerData:
          personalOfferContainer.poSiteShopHeaderResponse &&
          personalOfferContainer.poSiteShopHeaderResponse?.sponsor?.user,
        products: products,
        isExpired:
          personalOfferContainer?.personalOfferData?.expiresAt || false,
        shareLinks: personalOfferContainer?.personalOfferData?.shareLinks || {},
        status: personalOfferContainer?.personalOfferData?.responseStatus || '',
      },
      appConfigData: {
        directSellerAttributeId,
        directSellerAttributeValue,
        enablePersonalOfferPDP:
          APPConfig?.getAppConfig()?.enablePersonalOfferPDP || 'false',
        enableBuyNowForPO: convertToBoolean(
          APPConfig?.getAppConfig()?.enableBuyNowForPO
        ),
        enableProductOfferV2: convertToBoolean(
          APPConfig?.getAppConfig()?.enableProductOfferV2
        ),
      },
      routerHistory: this.props.history,
      enableNarrowSymbolCurrencyFormat,
      enableDecimalRoundingInPrice,
      excludeSpaceInPrice,
    }
    const { isEquinoxPOEnabled, hostInvalidSponsor } = personalOfferContainer
    const showNoOfferHere = hostInvalidSponsor || !!this.mfeNoOfferRequest
    const noOfferUserId = personalOfferContainer.getNoOfferUserId(
      this.mfeNoOfferRequest
    )
    const noOfferPitchId = personalOfferContainer.getNoOfferPitchId(
      this.mfeNoOfferRequest
    )
    const noOfferReason = personalOfferContainer.getNoOfferReason(
      this.mfeNoOfferRequest
    )

    const handleNoOfferStoreClick = () => {
      redirectToLocaleHome()
    }
    return (
      <>
        <MetaDecorator
          title={prTitle}
          imageUrl=""
          imageAlt=""
          description={prTitle}
          location={this.props}
        />
        <div
          className="personal-offer-mfe-container"
          data-testid="qa-personal-offer-mfe">
          {showNoOfferHere ? (
            <NoOfferHere
              onButtonClick={handleNoOfferStoreClick}
              userId={noOfferUserId}
              pitchId={noOfferPitchId}
              reason={noOfferReason}
            />
          ) : personalOfferContainer?.isPersonalOfferLoading ? (
            <PersonalOfferPDPLoader />
          ) : isEquinoxPOEnabled ? (
            <MicroFrontendLoader remoteConfig={remoteConfig} params={params} />
          ) : (
            <NotFound />
          )}
        </div>
        {getBuyNowForPO() && enablePORedesignFeature() && this.renderDrawer()}
        <SigninModal
          isVisible={this.isSigninModalVisible}
          onHideSignInModal={() => this.toggleSigninModal(false)}
          checkoutUrl={this.props}
        />
      </>
    )
  }
}
const PersonalOfferWithRouter = withRouter(PersonalOffer)
export default PersonalOfferWithRouter
export { PersonalOfferWithRouter as PersonalOffer }
